import { faFileMagnifyingGlass, faFileCircleXmark } from '@fortawesome/pro-light-svg-icons';
import ErrorDisplay from 'components/Error/ErrorDisplay';
import OrderDateFilter from 'components/Filters/OrderDateFilter';
import Search from 'components/Search/Search';
import Flex from 'components/ui/Flex';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import OrderResultsTable from 'components/Returns/OrderResultsTable';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReturnOrderHeader } from 'returnCenter-api';
import { selectedOrderSet, orderSelectors } from 'store/reducers/orderSlice';
import { clearReturnSlip as clearReturnSlipSet } from 'store/reducers/returnSlice';
import { styled } from 'styled-components';

type SearchOrdersProps = {
    className?: string;
};

const SearchOrders = ({ className }: SearchOrdersProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const orders = useAppSelector(orderSelectors.selectOrderHeaders);
    const loading = useAppSelector(orderSelectors.selectSearchLoading);
    const error = useAppSelector(orderSelectors.selectErrorMessage);

    const handleOrderSelect = useCallback(
        (order: ReturnOrderHeader) => {
            const orderId = order.orderNumber;
            if (!orderId) return;
            dispatch(clearReturnSlipSet());
            dispatch(selectedOrderSet(order));

            navigate(`/search/order/${orderId}`, {
                state: { orderIdFromPath: orderId },
            });
        },
        [dispatch, navigate],
    );

    const renderContent = () => {
        if (loading) {
            return (
                <Flex className="mt-3" column $grow={1} gap="1rem" align="center">
                    <Spinner useFullHeight center size="small" color="textFaded" />
                </Flex>
            );
        }

        if (orders === null) {
            return <ErrorDisplay errorMessage={t('searchForOrder')} icon={faFileMagnifyingGlass} />;
        }

        if (orders.length === 0 || error) {
            return <ErrorDisplay errorMessage={t('noOrderNumberFound')} icon={faFileCircleXmark} />;
        }

        return <OrderResultsTable orders={orders} onOrderSelect={handleOrderSelect} />;
    };

    return (
        <Container gap="1rem" className="create-return-wrapper" column $grow={1}>
            <Heading className={className} lineheight={20} fontSize={32} as="h2">
                {t('searchOrders')}
            </Heading>
            <Flex $grow={1} gap="0.5rem">
                <Search placeholder={`${t('eg')} 1234xxx...`} />
                <OrderDateFilter />
            </Flex>

            <Wrapper column>{renderContent()}</Wrapper>
        </Container>
    );
};

export default SearchOrders;

const Container = styled(Flex)`
    width: 100%;
`;

const Wrapper = styled(Flex)`
    flex: 1;
    flex-direction: column;
    gap: 1rem;
`;

const Heading = styled(Text)`
    padding: 0 !important;
`;
